import type { CreateTemplateData, WhatsAppTemplate } from '../services/api.ts';

export const TEMPLATE_USE_CASES: Record<string, string> = {
  form: 'Formulário', follow_up: 'Follow-up', confirmation: 'Confirmação de consulta',
  noshow: 'No-show', pos_consulta: 'Pós-consulta', pos_venda: 'Pós-venda',
  campaign: 'Campanha', chat_active: 'Chat ativo', general: 'Geral',
};
export const TEMPLATE_LANGUAGES: Record<string, string> = {
  pt_BR: 'Português (Brasil)', pt_PT: 'Português (Portugal)', en_US: 'Inglês (EUA)',
  en_GB: 'Inglês (Reino Unido)', es: 'Espanhol', es_AR: 'Espanhol (Argentina)',
  es_MX: 'Espanhol (México)', fr: 'Francês', it: 'Italiano', de: 'Alemão',
};
export type TemplateHeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type TemplateButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
export interface TemplateButtonDraft {
  type: TemplateButtonType;
  text: string;
  url?: string;
  phone_number?: string;
  example?: string;
}
export interface TemplateDraft {
  name: string;
  category: 'UTILITY' | 'MARKETING';
  language: string;
  useCase: string;
  headerFormat: TemplateHeaderFormat;
  headerText: string;
  headerExample: string;
  mediaHandle: string;
  body: string;
  bodyExamples: Record<string, string>;
  footer: string;
  buttons: TemplateButtonDraft[];
}
export const newTemplateDraft = (): TemplateDraft => ({
  name: '', category: 'UTILITY', language: 'pt_BR', useCase: 'general',
  headerFormat: 'NONE', headerText: '', headerExample: '', mediaHandle: '',
  body: '', bodyExamples: {}, footer: '', buttons: [],
});

export const MEDIA_RULES = {
  IMAGE: { accept: 'image/jpeg,image/png', types: ['image/jpeg', 'image/png'], maxMB: 5, label: 'JPG ou PNG · até 5 MB' },
  VIDEO: { accept: 'video/mp4', types: ['video/mp4'], maxMB: 16, label: 'MP4 · até 16 MB' },
  DOCUMENT: { accept: 'application/pdf', types: ['application/pdf'], maxMB: 100, label: 'PDF · até 100 MB' },
} as const;

export function validateTemplateFile(format: TemplateHeaderFormat, file: Pick<File, 'type' | 'size'>): string | null {
  if (!(format in MEDIA_RULES)) return 'Escolha um cabeçalho de imagem, vídeo ou documento.';
  const rule = MEDIA_RULES[format as keyof typeof MEDIA_RULES];
  if (!(rule.types as readonly string[]).includes(file.type)) return `Formato inválido. Use ${rule.label}.`;
  if (!file.size || file.size > rule.maxMB * 1024 * 1024) return `O arquivo precisa ter conteúdo e no máximo ${rule.maxMB} MB.`;
  return null;
}

export function positionalVariables(text: string): string[] {
  return Array.from(new Set(Array.from(text.matchAll(/\{\{([1-9]\d*)\}\}/g), match => match[1])))
    .sort((a, b) => Number(a) - Number(b));
}

function validateVariables(text: string, field: string, max?: number): string[] {
  const variables = positionalVariables(text);
  const errors: string[] = [];
  if (/[{}]/.test(text.replace(/\{\{[1-9]\d*\}\}/g, ''))) errors.push(`${field}: use variáveis numéricas como {{1}}, {{2}}.`);
  if (variables.some((value, index) => Number(value) !== index + 1)) errors.push(`${field}: numere as variáveis em sequência, começando em {{1}}.`);
  if (max !== undefined && variables.length > max) errors.push(`${field}: no máximo ${max} variável.`);
  return errors;
}

export function validateTemplateDraft(draft: TemplateDraft): string[] {
  const errors: string[] = [];
  if (!/^[a-z0-9_]{1,255}$/.test(draft.name)) errors.push('Nome: use letras minúsculas sem acentos, números e sublinhado (até 255 caracteres).');
  if (!['UTILITY', 'MARKETING'].includes(draft.category)) errors.push('Escolha uma categoria suportada.');
  if (!draft.language) errors.push('Selecione o idioma.');
  if (!draft.body.trim() || draft.body.length > 1024) errors.push('O corpo precisa ter de 1 a 1.024 caracteres.');
  errors.push(...validateVariables(draft.body, 'Corpo'));
  if (/^\s*\{\{|\}\}\s*$/.test(draft.body)) errors.push('Inclua texto antes da primeira e depois da última variável do corpo.');
  if (/\}\}\s*\{\{/.test(draft.body)) errors.push('Separe as variáveis do corpo com texto.');
  if (positionalVariables(draft.body).some(key => !draft.bodyExamples[key]?.trim())) errors.push('Preencha um exemplo fictício para cada variável do corpo.');
  if (draft.headerFormat === 'TEXT') {
    if (!draft.headerText.trim() || draft.headerText.length > 60 || /[\r\n]/.test(draft.headerText)) errors.push('O cabeçalho de texto precisa ter de 1 a 60 caracteres, em uma linha.');
    errors.push(...validateVariables(draft.headerText, 'Cabeçalho', 1));
    if (positionalVariables(draft.headerText).length && !draft.headerExample.trim()) errors.push('Preencha o exemplo da variável do cabeçalho.');
  }
  if (draft.headerFormat in MEDIA_RULES && !/^\d+:.+$/.test(draft.mediaHandle)) errors.push('Anexe e carregue um arquivo de exemplo para o cabeçalho.');
  if (draft.footer.length > 60 || /[{}\r\n]/.test(draft.footer)) errors.push('Rodapé: até 60 caracteres, sem variáveis ou quebras de linha.');
  if (draft.buttons.length > 10) errors.push('Use no máximo 10 botões.');
  if (draft.buttons.filter(button => button.type === 'URL').length > 2) errors.push('Use no máximo 2 botões de site.');
  if (draft.buttons.filter(button => button.type === 'PHONE_NUMBER').length > 1) errors.push('Use no máximo 1 botão de telefone.');
  const groups = draft.buttons.map(button => button.type === 'QUICK_REPLY' ? 'quick' : 'action')
    .filter((group, index, all) => index === 0 || group !== all[index - 1]);
  if (groups.length > 2) errors.push('Mantenha as respostas rápidas juntas, antes ou depois dos botões de ação.');
  const labels = new Set<string>();
  draft.buttons.forEach((button, index) => {
    const prefix = `Botão ${index + 1}`;
    if (!button.text.trim() || button.text.length > 25 || /[{}\r\n]/.test(button.text)) errors.push(`${prefix}: informe um texto de até 25 caracteres, sem variáveis.`);
    if (labels.has(button.text.trim())) errors.push(`${prefix}: use um texto diferente dos outros botões.`);
    labels.add(button.text.trim());
    if (button.type === 'PHONE_NUMBER' && !/^\+?[1-9]\d{6,14}$/.test(button.phone_number || '')) errors.push(`${prefix}: informe telefone internacional com DDI, somente números (ex.: +5511999999999).`);
    if (button.type === 'URL') {
      const url = button.url || '';
      const variables = positionalVariables(url);
      if (!isWebUrl(url.replace('{{1}}', 'exemplo')) || url.length > 2000) errors.push(`${prefix}: informe uma URL HTTP/HTTPS válida (até 2.000 caracteres).`);
      errors.push(...validateVariables(url, prefix, 1));
      if (variables.length && (!url.endsWith('{{1}}') || (url.match(/\{\{1\}\}/g) || []).length !== 1)) errors.push(`${prefix}: a URL aceita somente {{1}}, uma vez, ao final.`);
      if (variables.length && (!isWebUrl(button.example || '') || !button.example?.startsWith(url.replace('{{1}}', '')) || /[{}]/.test(button.example))) errors.push(`${prefix}: preencha a URL completa de exemplo, com o mesmo prefixo e sem variáveis.`);
    }
  });
  return Array.from(new Set(errors));
}

export function isWebUrl(value: string): boolean {
  try { const url = new URL(value); return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname) && !url.username && !url.password; }
  catch { return false; }
}

/** Builds the same supported components for submit and the live preview. Validation belongs to submit. */
export function buildTemplateComponents(draft: TemplateDraft): any[] {
  const components: any[] = [];
  if (draft.headerFormat === 'TEXT') {
    components.push({ type: 'HEADER', format: 'TEXT', text: draft.headerText,
      ...(positionalVariables(draft.headerText).length ? { example: { header_text: [draft.headerExample] } } : {}) });
  } else if (draft.headerFormat in MEDIA_RULES) {
    components.push({ type: 'HEADER', format: draft.headerFormat, example: { header_handle: [draft.mediaHandle] } });
  }
  const variables = positionalVariables(draft.body);
  components.push({ type: 'BODY', text: draft.body,
    ...(variables.length ? { example: { body_text: [variables.map(key => draft.bodyExamples[key] || '')] } } : {}) });
  if (draft.footer) components.push({ type: 'FOOTER', text: draft.footer });
  if (draft.buttons.length) components.push({ type: 'BUTTONS', buttons: draft.buttons.map(button => ({
    type: button.type, text: button.text,
    ...(button.type === 'PHONE_NUMBER' ? { phone_number: button.phone_number } : {}),
    ...(button.type === 'URL' ? { url: button.url, ...(positionalVariables(button.url || '').length ? { example: [button.example] } : {}) } : {}),
  })) });
  return components;
}

export function buildTemplatePayload(draft: TemplateDraft): CreateTemplateData {
  const errors = validateTemplateDraft(draft);
  if (errors.length) throw new Error(errors.join('\n'));
  return { name: draft.name, language: draft.language, category: draft.category, use_case: draft.useCase, components: buildTemplateComponents(draft) };
}

/** Fail closed rather than silently removing imported components or named parameters. */
export function templateEditorUnsupportedReason(template: Pick<WhatsAppTemplate, 'category' | 'components'>): string | null {
  if (!['UTILITY', 'MARKETING'].includes(template.category)) return 'Templates de autenticação são somente leitura neste editor.';
  if (!Array.isArray(template.components)) return 'Os componentes deste template não estão disponíveis.';
  const seen = new Set<string>();
  for (const component of template.components) {
    if (!component || !['HEADER', 'BODY', 'FOOTER', 'BUTTONS'].includes(component.type) || seen.has(component.type)) return 'Este template possui componentes avançados. Consulte-o aqui e gerencie-o na Meta.';
    seen.add(component.type);
    if (component.type === 'HEADER' && !['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)) return 'O formato deste cabeçalho é somente leitura neste editor.';
    if (typeof component.text === 'string' && validateVariables(component.text, 'Texto').length) return 'Este template usa variáveis não suportadas. O conteúdo original será preservado.';
    if (component.type === 'BUTTONS' && (!Array.isArray(component.buttons) || component.buttons.some((button: any) => !['QUICK_REPLY', 'URL', 'PHONE_NUMBER'].includes(button.type) || (button.url && validateVariables(button.url, 'URL', 1).length)))) return 'Este template possui botões avançados. A edição está disponível na Meta.';
    const allowed = component.type === 'HEADER' ? ['type', 'format', 'text', 'example'] : component.type === 'BUTTONS' ? ['type', 'buttons'] : ['type', 'text', 'example'];
    if (Object.keys(component).some(key => !allowed.includes(key))) return 'Este template possui opções avançadas não editáveis aqui.';
    if (component.type === 'BUTTONS' && component.buttons.some((button: any) => Object.keys(button).some(key => !['type', 'text', 'url', 'phone_number', 'example'].includes(key)))) return 'Este template possui opções avançadas de botão não editáveis aqui.';
  }
  return seen.has('BODY') ? null : 'Este template não possui um corpo de mensagem editável.';
}

export function templateToDraft(template: WhatsAppTemplate): TemplateDraft {
  const header = template.components.find(component => component.type === 'HEADER');
  const body = template.components.find(component => component.type === 'BODY');
  return { ...newTemplateDraft(), name: template.name, category: template.category === 'MARKETING' ? 'MARKETING' : 'UTILITY',
    language: template.language, useCase: template.use_case || 'general',
    headerFormat: header?.format || 'NONE', headerText: header?.text || '', headerExample: header?.example?.header_text?.[0] || '',
    // Meta may return a public preview URL instead of a reusable upload handle.
    mediaHandle: /^\d+:.+$/.test(header?.example?.header_handle?.[0] || '') ? header.example.header_handle[0] : '',
    body: body?.text || '', bodyExamples: Object.fromEntries(positionalVariables(body?.text || '').map((key, index) => [key, body?.example?.body_text?.[0]?.[index] || ''])),
    footer: template.components.find(component => component.type === 'FOOTER')?.text || '',
    buttons: (template.components.find(component => component.type === 'BUTTONS')?.buttons || []).map((button: any) => ({ type: button.type, text: button.text, url: button.url, phone_number: button.phone_number, example: button.example?.[0] || '' })),
  };
}

export function templateApiError(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  if (Array.isArray(detail)) return detail.map(item => item.msg).filter(Boolean).join(' · ') || fallback;
  return fallback;
}
